import React, { useState } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Badge,
  Collapse,
  Button,
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { HANDOFF_REASONS } from '../../config/aiConversationStatus';

// The double records four operations and they are not interchangeable: a
// deletion shown as "Would write", with the null payload every deletion
// carries, is the engine's most consequential act rendered as its least.
// The colour stays `warning` for all four — `danger` here would read as a
// failure, and a deliberate delete is not one.
const WRITE_WORDS = {
  set: 'Would create',
  'set-merge': 'Would merge into',
  update: 'Would update',
  delete: 'Would delete',
};

// The scheduler's three verbs, which the engine stores in its own vocabulary.
const SCHEDULER_ACTIONS = {
  reply: 'Replied',
  followUp: 'Sent a follow-up',
  handoff: 'Handed over to the call center',
};

const Line = ({ colour, title, children }) => (
  <div className="border-left pl-2 mb-3" style={{ borderLeftWidth: 3 }}>
    <div>
      <Badge color={colour}>{title}</Badge>
    </div>
    <div className="font-size-12 mt-1">{children}</div>
  </div>
);

const Json = ({ value }) => (
  <pre
    className="mb-0 font-size-11 bg-light p-2 rounded"
    style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
  >
    {JSON.stringify(value, null, 2)}
  </pre>
);

const Prompt = ({ text }) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  return (
    <>
      {/* A button, not an anchor: this goes nowhere, and an href="#" is both
          announced as a link and a page jump away from a missed
          preventDefault. */}
      <Button color="link" className="p-0" onClick={() => setOpen(!open)}>
        {open ? t('Hide the prompt') : t('Show the prompt sent')}
      </Button>
      <Collapse isOpen={open}>
        <pre
          className="mt-2 mb-0 font-size-11 bg-light p-2 rounded"
          style={{ whiteSpace: 'pre-wrap' }}
        >
          {text}
        </pre>
      </Collapse>
    </>
  );
};

// The country's clock, which is the one the scheduler reasons on: the
// browser's would show an agent following up at an hour it never chose.
const FALLBACK_TIME_ZONE = 'Europe/Rome';

const Event = ({ event, timezone }) => {
  const { t } = useTranslation();

  switch (event.type) {
    case 'opening':
      return (
        <Line colour="primary" title={t('Opening message')}>
          <div>
            {event.source === 'template'
              ? `${t('Template')} ${event.templateName} (${
                  event.templateLanguage
                })`
              : t('Direct message configured on the agent')}
          </div>
          {!!event.params.length && (
            <div className="text-muted">
              {t('Placeholders')}: {event.params.join(', ')}
            </div>
          )}
          <div className="mt-1">{event.text}</div>
        </Line>
      );

    case 'toolCall':
      return (
        <Line colour={event.ok ? 'success' : 'danger'} title={event.name}>
          {!event.ok && (
            <div className="text-danger mb-1">
              {t('Refused by the engine')}:{' '}
              {(event.result && event.result.error) || ''}
            </div>
          )}
          <div className="text-muted">{t('Parameters')}</div>
          <Json value={event.params} />
          <div className="text-muted mt-1">{t('Result')}</div>
          <Json value={event.result} />
        </Line>
      );

    case 'write':
      return (
        <Line
          colour="warning"
          title={`${t(WRITE_WORDS[event.operation] || 'Would write')} → ${
            event.path
          }`}
        >
          <Json value={event.data} />
        </Line>
      );

    case 'schedulerDecision':
      return (
        <Line
          colour="info"
          title={`${t('Scheduler')}: ${t(
            SCHEDULER_ACTIONS[event.action] || event.action,
          )}`}
        >
          {/* A handoff's reason is always an engine constant the lead page
              already humanises; every other reason is Italian prose the
              engine wrote. Map what maps, print the rest. */}
          <div>
            {HANDOFF_REASONS[event.reason]
              ? t(HANDOFF_REASONS[event.reason])
              : event.reason}
          </div>
          {/* atMs is the simulated clock's time, shown in the country's
              time zone, the one the scheduler decides on. */}
          <div className="text-muted">
            {moment(event.atMs)
              .tz(timezone || FALLBACK_TIME_ZONE)
              .format('DD/MM HH:mm:ss')}
          </div>
        </Line>
      );

    case 'extraction':
      return (
        <Line colour="secondary" title={t('Final extraction')}>
          <div className="text-muted">{t('Accepted')}</div>
          <Json value={event.accepted} />
          <div className="text-muted mt-1">{t('Dropped')}</div>
          <Json value={event.dropped} />
        </Line>
      );

    case 'cost':
      return (
        <Line colour="light" title={t('Cost')}>
          ${Number(event.usd).toFixed(5)}{' '}
          {event.model ? `· ${event.model}` : ''}
        </Line>
      );

    case 'prompt':
      return (
        <Line colour="light" title={t('Prompt')}>
          <Prompt text={event.text} />
        </Line>
      );

    case 'modelError':
      return (
        <Line colour="danger" title={t('Model error')}>
          {event.message}
        </Line>
      );

    // Our defect, not theirs: the allowlist is missing a path a legitimate
    // simulation needs. Said plainly so nobody spends an afternoon hunting
    // for it in their acquisition order. Deliberately NOT phrased as a
    // health check — the engine's own reads of aiConversations and messages
    // are refused just as often and never reach this event, so an empty
    // panel says nothing either way.
    case 'refusedRead':
      return (
        <Line colour="dark" title={t('Simulator gap')}>
          <div>
            {t(
              'The simulator refused to read a configuration document. This is a defect in the simulator, not in the configuration being tested.',
            )}
          </div>
          <div className="text-muted mt-1">{event.path}</div>
        </Line>
      );

    // A call the engine refused to run because the turn had already ended at
    // an earlier outcome in the same batch. On close_positive the parameters
    // ARE the offers the lead accepted and nothing closed, which is why this
    // is `danger` and not `warning`: it is a sale that went missing, not a
    // step that was skipped. Without this branch the event fell through to
    // `default` and showed as a grey JSON blob.
    case 'discardedToolCall':
      return (
        <Line colour="danger" title={`${t('Call discarded')}: ${event.name}`}>
          <div>
            {t(
              'The engine had already closed the turn on an earlier outcome, so this call never ran. If it was a closure, the offers below were accepted and nothing recorded them.',
            )}
          </div>
          <div className="text-muted mt-1">{t('Parameters')}</div>
          <Json value={event.params} />
        </Line>
      );

    default:
      return (
        <Line colour="light" title={event.type}>
          <Json value={event} />
        </Line>
      );
  }
};

const SimulatorPanel = ({ events, timezone }) => {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader>{t('What the engine is doing')}</CardHeader>
      <CardBody style={{ height: '75vh', overflowY: 'auto' }}>
        {!events.length && (
          <p className="text-muted">
            {t('Nothing yet: start the conversation.')}
          </p>
        )}
        {events.map((event, index) => (
          <Event key={index} event={event} timezone={timezone} />
        ))}
      </CardBody>
    </Card>
  );
};

export default SimulatorPanel;
